const SPLIT_SEGMENT_LABEL = /\s\(([^)]+)\)$/;
export const ROOM_ASSIGNMENT_MIGRATION_VERSION = 3;

const ROOM_ASSIGNMENT_COMPONENT_TYPES = ["basement", "crawlspace", "slab", "wall", "ceiling", "expFloor"];
const FOUNDATION_PERIMETER_TYPES = new Set(["basement", "crawlspace", "slab"]);

const getMigrationVersion = (uiSettings = {}) =>
    Math.max(uiSettings.roomAssignmentMigrationVersion || 0, uiSettings.foundationBasementRoomMigrationVersion || 0);

const findRoomIdByName = (roomSummary = {}, catId, roomName = "") => {
    const catRooms = roomSummary[catId] || {};

    const match = Object.entries(catRooms).find(
        ([roomId, { name } = {}]) => !roomId.startsWith("room-default") && name === roomName
    );

    return match?.[0] || null;
};

const isSplitSegmentLabel = (label = "") => SPLIT_SEGMENT_LABEL.test(label);

const restoreSplitSegmentRoomAssignment = (comp, roomSummary = {}, catId) => {
    const labelMatch = (comp.label || "").match(SPLIT_SEGMENT_LABEL);

    if (!labelMatch) {
        return false;
    }

    const roomId = findRoomIdByName(roomSummary, catId, labelMatch[1]);

    if (!roomId) {
        return false;
    }

    comp.roomId = roomId;
    return true;
};

const clampFoundationExposedPerimeter = (comp) => {
    const totalPerimeter = parseFloat(comp.floor?.measurements?.perimeter || 0);
    const exposed = parseFloat(comp.exposedSurfacePerimeter || 0);

    if (totalPerimeter > 0 && exposed > totalPerimeter) {
        comp.exposedSurfacePerimeter = totalPerimeter;
    }
};

const normalizeCategoryComponentGroup = (components = [], category, roomSummary = {}, componentType) => {
    const defaultRoomId = `room-default-${category}`;

    if (FOUNDATION_PERIMETER_TYPES.has(componentType)) {
        components.forEach((comp) => clampFoundationExposedPerimeter(comp));
    }

    if (components.length !== 1) {
        components.forEach((comp) => {
            if (isSplitSegmentLabel(comp.label) && comp.roomId === defaultRoomId) {
                restoreSplitSegmentRoomAssignment(comp, roomSummary, category);
            }
        });

        return;
    }

    const [comp] = components;

    if (isSplitSegmentLabel(comp.label)) {
        restoreSplitSegmentRoomAssignment(comp, roomSummary, category);
        return;
    }

    if (!comp.roomId || comp.roomId.startsWith("room-default")) {
        return;
    }

    comp.roomId = defaultRoomId;
};

/** Move single imported aggregates to Not Assigned per storey; preserve split segment room assignments. */
export const normalizeImportedRoomAssignments = (modelData = {}) => {
    const components = modelData.components || {};
    const roomSummary = modelData.roomSummary || {};

    ROOM_ASSIGNMENT_COMPONENT_TYPES.forEach((componentType) => {
        const typeComponents = components[componentType] || {};
        const byCategory = {};

        Object.values(typeComponents).forEach((comp) => {
            if (!comp?.category) {
                return;
            }

            if (!byCategory[comp.category]) {
                byCategory[comp.category] = [];
            }

            byCategory[comp.category].push(comp);
        });

        Object.entries(byCategory).forEach(([category, categoryComponents]) => {
            normalizeCategoryComponentGroup(categoryComponents, category, roomSummary, componentType);
        });
    });

    return modelData;
};

export const applyImportedRoomMigrationIfNeeded = (modelData = {}) => {
    if (getMigrationVersion(modelData.uiSettings) >= ROOM_ASSIGNMENT_MIGRATION_VERSION) {
        return modelData;
    }

    normalizeImportedRoomAssignments(modelData);
    modelData.uiSettings = {
        ...modelData.uiSettings,
        roomAssignmentMigrationVersion: ROOM_ASSIGNMENT_MIGRATION_VERSION,
        foundationBasementRoomMigrationVersion: ROOM_ASSIGNMENT_MIGRATION_VERSION,
        foundationBasementRoomNormalized: true,
    };

    return modelData;
};

/** @deprecated Use normalizeImportedRoomAssignments */
export const normalizeBasementRoomAssignments = normalizeImportedRoomAssignments;

/** @deprecated Use applyImportedRoomMigrationIfNeeded */
export const applyBasementRoomMigrationIfNeeded = applyImportedRoomMigrationIfNeeded;

/** @deprecated Use ROOM_ASSIGNMENT_MIGRATION_VERSION */
export const BASEMENT_ROOM_MIGRATION_VERSION = ROOM_ASSIGNMENT_MIGRATION_VERSION;
